import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useUser } from '../../context/UserContext';
import { BellRing } from 'lucide-react';

// A hidden component that polls for new human handoff requests globally
const GlobalHandoffListener = ({ onNotificationClick }) => {
    const { user } = useUser();

    // Keep track of thread IDs we've already notified about so we don't spam the user
    // Using a ref so it doesn't trigger re-renders
    const notifiedThreadsRef = useRef(new Set());

    // Synthesize a clean "ding" sound using Web Audio API
    // No external mp3 files required!
    const playNotificationSound = () => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;

            const context = new AudioContext();

            // Oscillator for the tone
            const osc = context.createOscillator();
            const gainNode = context.createGain();

            osc.connect(gainNode);
            gainNode.connect(context.destination);

            // A pleasant "ding" sound (high pitch sine wave)
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, context.currentTime); // A5 note
            osc.frequency.exponentialRampToValueAtTime(1760, context.currentTime + 0.1); // Slide up to A6

            // Volume envelope (quick attack, slow decay)
            gainNode.gain.setValueAtTime(0, context.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.5, context.currentTime + 0.05);
            gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.5);

            osc.start(context.currentTime);
            osc.stop(context.currentTime + 0.5);
        } catch (e) {
            console.warn("Audio Context not supported or blocked by browser:", e);
        }
    };

    useEffect(() => {
        if (!user) return;

        const checkHandoffRequests = async () => {
            try {
                const token = await user.getIdToken();
                // Fetch only threads that need attention (human handoff requested)
                const response = await axios.get('/api/threads', {
                    params: {
                        status: 'needs_attention',
                        limit: 10 // Only grab recent ones
                    },
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const activeRequests = response.data.threads || [];

                activeRequests.forEach(thread => {
                    // If we haven't seen this thread request yet
                    if (!notifiedThreadsRef.current.has(thread.thread_id)) {
                        // Mark as seen
                        notifiedThreadsRef.current.add(thread.thread_id);

                        // Prevent notifying for old requests that were left open when we first log in
                        // Only notify if the request was updated in the last 2 minutes
                        const lastMessageTime = new Date(thread.last_message_at).getTime();
                        const now = Date.now();
                        const twoMinutesAgo = now - (2 * 60 * 1000);

                        if (lastMessageTime > twoMinutesAgo) {
                            playNotificationSound();

                            const customerName = thread.customer?.name ||
                                (thread.customer?.email ? thread.customer.email.split('@')[0] : 'A visitor');
                            const botName = thread.chatbot_name || 'your chatbot';

                            toast.custom((t) => (
                                <div className="bg-white border text-slate-800 border-slate-200 shadow-lg rounded-xl p-4 flex gap-4 cursor-pointer hover:bg-slate-50 transition-colors w-[356px]"
                                    onClick={() => {
                                        toast.dismiss(t);
                                        onNotificationClick(thread.chatbot_id, thread.thread_id);
                                    }}
                                >
                                    <div className="bg-orange-100 text-orange-600 p-2 rounded-full h-fit mt-1">
                                        <BellRing size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-sm">Human Required</h3>
                                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                            <span className="font-medium text-slate-700">{customerName}</span> needs help on <span className="font-medium text-slate-700">{botName}</span>.
                                        </p>
                                        <div className="text-[10px] text-indigo-600 font-medium mt-2 flex items-center gap-1">
                                            Click to open in Inbox &rarr;
                                        </div>
                                    </div>
                                </div>
                            ), {
                                duration: 8000,
                                position: 'bottom-right',
                            });
                        }
                    }
                });

            } catch (err) {
                console.error("Failed to poll global handoff requests", err);
            }
        };

        // Poll every 10 seconds
        const pollInterval = setInterval(checkHandoffRequests, 10000);

        // Initial check
        checkHandoffRequests();

        return () => clearInterval(pollInterval);
    }, [user, onNotificationClick]);

    return null; // This component has no UI of its own, it just polls and triggers toasts
};

export default GlobalHandoffListener;
